import { useEffect, useMemo, useState } from 'react';
import type { BulkEditAssignment, LowRiskFieldValue } from '@complan/contracts';

import { ApiRequestError } from '@/shared/api/api-error';
import { findingsFromError } from '@/features/admin/admin-findings';
import { useIntakeOptions } from '@/features/initiative/intake/intake-options-data';

import { BulkEditDialog } from './BulkEditDialog';
import { useBulkEditApply, useBulkEditPreview } from './bulk-edit-data';

/**
 * Owns one Bulk_Edit interaction: preview, read, apply
 * (`complan-governance` R10.4, R10.9, R10.15).
 *
 * Separated from the list page because the interaction has its own state machine - an
 * assignment, a preview response, a token, a conflict - and the page has enough to do
 * holding the selection. The dialog stays presentational: it renders what it is given and
 * reports what the reader chose.
 *
 * ## The token is the whole point of the two steps
 *
 * R10.15 makes an apply that was not previewed in the same interaction refusable. The
 * preview issues a token and the observed versions; the apply sends both back. So this
 * component discards the preview whenever the assignment or the selection changes: a token
 * describes *those* changes to *those* versions, and reusing it against anything else is
 * exactly what the criterion forbids.
 *
 * ## A conflict is not an error to retry
 *
 * R10.9's version conflict and R10.15's unpreviewed apply both come back as a `conflict`,
 * and the remedy for both is the same: preview again, because the records moved under the
 * reader. The dialog says that rather than offering a retry that would fail identically.
 */
export interface BulkEditControllerProps {
  readonly isOpen: boolean;
  readonly selectedIds: ReadonlySet<string>;
  readonly onClose: () => void;
  readonly onApplied: () => void;
}

/** See the `isStale` note in {@link BulkEditController}. */
function isStalePreviewRefusal(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) {
    return false;
  }
  if (error.body.code === 'conflict') {
    return true;
  }
  // Narrowed through the discriminant, because `fieldIssues` exists on exactly one member
  // of the error union.
  return (
    error.body.code === 'validation_failed' &&
    error.body.fieldIssues.some((issue) => issue.path === 'previewToken')
  );
}

export function BulkEditController({
  isOpen,
  selectedIds,
  onClose,
  onApplied,
}: BulkEditControllerProps): React.JSX.Element {
  const preview = useBulkEditPreview();
  const apply = useBulkEditApply();
  const [assignment, setAssignment] = useState<BulkEditAssignment | null>(null);

  /**
   * The selectable values for the five fields R10.1 admits.
   *
   * `useIntakeOptions` already resolves all five vocabularies - priorities, categories,
   * Initiative types and the assignable accounts - with the inactive-but-referenced
   * handling of R5.9 and the display-name disambiguation a real directory needs. Reusing
   * it costs a few reads this dialog does not use and saves a second, subtly different
   * owner list; the intake form's version is the one that has been got right, including
   * the "two accounts with one display name" case.
   */
  const intakeOptions = useIntakeOptions();

  const options = useMemo(() => {
    const sets = intakeOptions.options;
    const toOptions = (
      records: readonly { readonly id: string; readonly label: string }[],
    ): readonly { id: string; name: string }[] =>
      records.map((record) => ({ id: record.id, name: record.label }));
    const people = toOptions(sets.owners);
    return {
      priorityId: toOptions(sets.priorities),
      categoryId: toOptions(sets.categories),
      initiativeTypeId: toOptions(sets.initiativeTypes),
      ownerUserId: people,
      backupOwnerUserId: people,
    } satisfies Record<LowRiskFieldValue, readonly { id: string; name: string }[]>;
  }, [intakeOptions.options]);

  // Opening afresh, or changing what is selected, invalidates any preview in hand: it
  // described a different set of records (R10.15).
  const selectionKey = [...selectedIds].sort().join(',');
  useEffect(() => {
    preview.reset();
    apply.reset();
    setAssignment(null);
    // Only `isOpen` and the selection: `preview` and `apply` are fresh objects on every
    // render, so listing them would re-run this effect continuously and wipe the preview
    // the reader just asked for, one render after it arrived.
  }, [isOpen, selectionKey]);

  /**
   * True when the server refused because the preview no longer describes the data.
   *
   * Two wire shapes, one remedy. A replayed or superseded token is a `409` carrying
   * `conflict: { kind: 'stale_preview' }` (R10.9, R10.15). A token the server never issued
   * is a `422` whose only content is a `previewToken` field issue - no `findings` at all,
   * which is why it has to be recognised here: `FindingsAlert` would render nothing and the
   * reader would press Apply and see no response whatsoever. Live verification is what
   * surfaced that; the UI cannot reach the case today (there is no apply without a
   * preview), but a silent failure is the wrong thing to leave behind a guard.
   */
  const isStale = isStalePreviewRefusal(apply.error);

  return (
    <BulkEditDialog
      isOpen={isOpen}
      selectedCount={selectedIds.size}
      options={options}
      preview={preview.data}
      isPreviewing={preview.isPending}
      isApplying={apply.isPending}
      previewFindings={findingsFromError(preview.error)}
      applyFindings={findingsFromError(apply.error)}
      isStale={isStale}
      onPreview={(next) => {
        setAssignment(next);
        // A new preview supersedes any refusal from the last apply attempt.
        apply.reset();
        preview.mutate({ initiativeIds: [...selectedIds], assignments: [next] });
      }}
      onApply={(reason) => {
        const response = preview.data;
        if (response === undefined || assignment === null) {
          return;
        }
        apply.mutate(
          {
            previewToken: response.previewToken,
            // The assignments the *preview* echoed, not the local state: if the two ever
            // differed, the server's own record of what was previewed is the one to honour.
            assignments: response.assignments,
            // R10.9: the versions the preview observed, so a record changed since then is
            // rejected rather than silently overwritten.
            expectedVersions: response.rows.map((row) => ({
              initiativeId: row.initiativeId,
              version: row.version,
            })),
            reason,
          },
          { onSuccess: onApplied },
        );
      }}
      onClose={onClose}
    />
  );
}
